import React, { useMemo, useCallback, useState } from "react";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { getRandomStatusText } from "./utils";
import { getStatusIcon } from "./StatusIcon";
import { FilepathLink } from "../FilepathLink";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface ClaudeCodeEditToolBlockProps {
  args: {
    file_path: string;
    old_string: string;
    new_string: string;
  };
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  toolResult?: any;
  toolCallId?: string;
  messageId: string;
  isLastMessage?: boolean;
  isChatLoading?: boolean;
}

export const ClaudeCodeEditToolBlock: React.FC<ClaudeCodeEditToolBlockProps> =
  React.memo(
    ({
      args,
      status,
      toolResult,
      toolCallId,
      messageId,
      isLastMessage = false,
      isChatLoading = false,
    }) => {
      const openCodeDiffPanel = useAiContextStore(
        (state) => state.openCodeDiffPanel,
      );
      const setNewLayout = useLayoutStore((store) => store.setNewLayout);

      const [accordionValue, setAccordionValue] = useState<string>("");

      const { hasDiffData, fileEdited, errorMessage, activityId } =
        useMemo(() => {
          // Extract toolUseResult from parsed tool result
          let parsedToolResult = null;
          if (toolResult && typeof toolResult === "string") {
            try {
              const parsed = JSON.parse(toolResult);
              parsedToolResult = parsed.result || parsed;
            } catch {
              // Fall through to use raw toolResult
            }
          } else {
            parsedToolResult = toolResult;
          }

          const toolUseResult = (parsedToolResult as any)?.toolUseResult;
          const filePath = args?.file_path || toolUseResult?.filePath || "";
          const activityId = (parsedToolResult as any)?.activityId;

          const hasDiffData = activityId !== undefined && filePath;

          // Extract error message for failed status
          let errorMessage =
            (parsedToolResult as any)?.message ||
            (status === "failed"
              ? "Edit tool execution failed"
              : status === "aborted"
                ? "Edit tool execution aborted"
                : null);

          // Remove tool_use_error XML tags from error message
          if (errorMessage && typeof errorMessage === "string") {
            errorMessage = errorMessage
              .replace(/<\/?tool_use_error>/g, "")
              .trim();
          }

          return {
            hasDiffData,
            fileEdited: filePath,
            errorMessage,
            activityId,
          };
        }, [args, toolResult, status]);

      const statusIcon = useMemo(() => getStatusIcon(status), [status]);

      const handleDiffClick = useCallback(() => {
        if (activityId) {
          openCodeDiffPanel(activityId);
          setTimeout(() => {
            setNewLayout(LeftTab.EngSpec);
          }, 50);
        }
      }, [activityId, openCodeDiffPanel, setNewLayout]);

      // Shared container styles
      const containerClasses =
        "rounded-md text-xs my-2 bg-secondary-n-950 text-secondary-n-400 transition-colors duration-200 border border-[#2F3035]";

      // Shared file path rendering
      const renderFilePath = () => (
        <div className="flex items-center min-w-0">
          {fileEdited ? (
            <button
              className="text-xs truncate hover:text-secondary-n-200 cursor-pointer text-left"
              onClick={handleDiffClick}
              disabled={!hasDiffData}
            >
              {fileEdited}
            </button>
          ) : (
            <span className="text-xs text-secondary-n-500">
              {getRandomStatusText()}
            </span>
          )}
        </div>
      );

      const renderDiffButton = () =>
        fileEdited &&
        hasDiffData &&
        activityId && (
          <FilepathLink
            onClick={handleDiffClick}
            ariaLabel={`View diff for ${fileEdited}`}
            tooltipContent={fileEdited}
            className="shrink-0"
          />
        );

      // Utility functions for accordion styling
      const getAccordionTriggerClasses = () =>
        cn(
          "flex items-center text-xs font-medium text-secondary-n-400 px-0 py-1.5",
          "hover:no-underline",
          "[&[data-state=closed]>svg:last-child]:hidden [&[data-state=open]>svg:last-child]:hidden",
        );

      const getChevronClasses = () =>
        cn(
          "w-4 h-4 text-secondary-n-400 transition-all absolute inset-0",
          accordionValue === "tool-content"
            ? "rotate-90 opacity-100"
            : "opacity-0 group-hover/edit:opacity-100",
        );

      const getToolHeading = () => {
        if (status === "in-progress") {
          return "Editing file...";
        }
        return fileEdited ? `${fileEdited}` : "Edit File";
      };

      // For successful edits, show simple inline layout
      if (status === "succeeded" || status === "in-progress") {
        return (
          <div
            className={cn(
              containerClasses,
              "px-2 py-2 flex flex-col items-start justify-start group",
            )}
          >
            <div className="w-full grid grid-cols-[auto_minmax(0,1fr)_auto] gap-1 items-center">
              {statusIcon}
              {renderFilePath()}
              <div className="flex items-center gap-1 shrink-0">
                {renderDiffButton()}
              </div>
            </div>
          </div>
        );
      }

      // For non-successful statuses (failed, aborted), show accordion
      return (
        <Accordion
          type="single"
          value={accordionValue}
          onValueChange={setAccordionValue}
          collapsible
          className={cn(containerClasses, "group/edit")}
        >
          <AccordionItem value="tool-content" className="border-none">
            <AccordionTrigger
              className={cn(getAccordionTriggerClasses(), "px-2 py-2")}
            >
              <div className="w-full grid grid-cols-[auto_minmax(0,1fr)] gap-2 items-center">
                <div className="relative w-4 h-4">
                  <ChevronRightIcon className={getChevronClasses()} />
                  <div
                    className={cn(
                      "absolute inset-0 transition-opacity",
                      accordionValue === "tool-content"
                        ? "opacity-0"
                        : "opacity-100 group-hover/edit:opacity-0",
                    )}
                  >
                    {statusIcon}
                  </div>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-xs truncate">{getToolHeading()}</span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="transition-all relative ml-4">
              <div className="absolute left-[-18px] top-0 bottom-0 w-px bg-secondary-n-700"></div>
              {/* Error message for failed/aborted status */}
              {errorMessage && (
                <div className="text-xs text-red-400 px-2 py-2 mb-1 leading-relaxed">
                  {errorMessage}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    },
  );
